import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { PropertyFormData } from "./PropertyForm";
import { PreviewInput } from "../../atoms/PreviewInput";
import { Textarea } from "../../atoms/Textarea";
import { PreviewCheckbox } from "../../atoms/PreviewCheckbox";

// Helper to check if a URL is a shortened Google Maps URL that needs resolution
const isShortGoogleMapsUrl = (link: string): boolean => {
  if (!link) return false;
  return link.includes('maps.app.goo.gl') || link.includes('goo.gl/maps');
};

// Helper to extract a usable embed URL from various Google Maps link formats
const getGoogleMapEmbedSrc = (link: string): string => {
  if (!link) return '';

  // Case 0: User pasted the full iframe code
  // Regex to extract src from <iframe ... src="...">
  const iframeSrcMatch = link.match(/src="([^"]+)"/);
  if (iframeSrcMatch) {
    return iframeSrcMatch[1];
  }

  // Case 0.5: User pasted a direct embed link (usually has /embed? or /maps/embed)
  if (link.includes('/embed') || link.includes('output=embed')) {
    return link;
  }

  // Case 1: Shortened Google Maps URL - return empty, will be resolved via API
  if (isShortGoogleMapsUrl(link)) {
    return '';
  }

  // Case 2: Coordinates in URL arguments (e.g. ?q=11.55,104.92)
  const queryCoordsMatch = link.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (queryCoordsMatch) {
    return `https://www.google.com/maps?q=${queryCoordsMatch[1]},${queryCoordsMatch[2]}&output=embed`;
  }

  // Case 3: Coordinates in URL path (e.g. /@11.55,104.92)
  const pathCoordsMatch = link.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (pathCoordsMatch) {
    return `https://www.google.com/maps?q=${pathCoordsMatch[1]},${pathCoordsMatch[2]}&output=embed`;
  }

  // Case 4: Place name in URL (e.g. /place/Royal+Palace)
  const placeMatch = link.match(/\/place\/([^/]+)/);
  if (placeMatch) {
    return `https://www.google.com/maps?q=${placeMatch[1]}&output=embed`;
  }

  // Fallback: Try to use the link itself as a query
  return `https://www.google.com/maps?q=${encodeURIComponent(link)}&output=embed`;
};

export const DetailPropertyForm: React.FC<{ property: PropertyFormData }> = ({ property }) => {
  const [resolvedMapUrl, setResolvedMapUrl] = useState<string | null>(null);
  const [isResolvingMap, setIsResolvingMap] = useState(false);

  // Resolve shortened Google Maps URLs via backend API
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    // Immediately clear the old map to prevent showing stale data
    setResolvedMapUrl(null);

    const resolveMapUrl = async () => {
      if (!property.googleMapLink) {
        return;
      }

      // If it's a shortened URL, resolve via backend
      if (isShortGoogleMapsUrl(property.googleMapLink)) {
        setIsResolvingMap(true);
        try {
          const apiBase = process.env.REACT_APP_API_BASE || 'http://127.0.0.1:8000/api/';
          // Remove trailing slash if present, then add the endpoint
          const baseUrl = apiBase.replace(/\/$/, '');
          const response = await fetch(`${baseUrl}/resolve-google-maps-url`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ url: property.googleMapLink }),
            signal, // Pass the abort signal
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.embed_url) {
              setResolvedMapUrl(data.embed_url);
            } else {
              setResolvedMapUrl(null);
            }
          } else {
            setResolvedMapUrl(null);
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            // Silence abort errors
            return;
          }
          console.error('Error resolving map URL:', error);
          setResolvedMapUrl(null);
        } finally {
          setIsResolvingMap(false);
        }
      } else {
        // For non-shortened URLs, use the existing helper
        const embedSrc = getGoogleMapEmbedSrc(property.googleMapLink);
        setResolvedMapUrl(embedSrc || null);
      }
    };

    resolveMapUrl();

    // Cleanup: abort the request if the component unmounts or property changes
    return () => {
      controller.abort();
    };
  }, [property.googleMapLink]);

  // Helper to build full image URL if needed
  const buildImageUrl = (imagePath: string) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    // Use env variable or fallback to current origin
    const mediaBase = process.env.REACT_APP_MEDIA_URL;
    if (imagePath.startsWith('/media/')) {
      return `${mediaBase}${imagePath}`;
    }
    return imagePath;
  };
  return (
    <>
      <StyledHeaderRow>
        <PreviewCheckbox
          name="isAvailable"
          label="Is Available?"
          checked={property.isAvailable}
          variant="empty"
        />
      </StyledHeaderRow>
      <StyledFormGrid>
        <PreviewInput label="Property Code" name="propertyCode" value={property.propertyCode} fullWidth />
        <PreviewInput label="Property Name" name="propertyName" value={property.propertyName} fullWidth />
        <PreviewInput label="Property Category" name="propertyCategory" value={property.propertyCategory} fullWidth />
        <PreviewInput label="Property Type" name="propertyType" value={property.propertyType} fullWidth />
        <PreviewInput label="Property Status" name="propertyStatus" value={property.propertyStatus} fullWidth />
        <PreviewInput label="Owner" name="owner" value={property.owner} fullWidth />
        <PreviewInput label="City" name="city" value={property.city} fullWidth />
        <PreviewInput label="District" name="district" value={property.district} fullWidth />
        <PreviewInput label="Commune" name="commune" value={property.commune} fullWidth />
        <PreviewInput label="Price" name="price" value={property.price ? parseInt(property.price, 10).toLocaleString() : ''} fullWidth />
        <PreviewInput label="Size" name="size" value={property.size} fullWidth />
        <PreviewInput label="Land Area" name="landArea" value={property.landArea} fullWidth />
        <PreviewInput label="Bedrooms" name="bedrooms" value={property.bedrooms} fullWidth />
        <PreviewInput label="Bathrooms" name="bathrooms" value={property.bathrooms} fullWidth />
        <PreviewInput label="Popular Place" name="popularPlace" value={property.popularPlace} fullWidth />
        <StyledGridSpan2>
          <PreviewInput label="Features" name="features" value={property.features?.join(', ') || '--'} fullWidth />
        </StyledGridSpan2>
        <StyledLinkContainer>
          <StyledLinkLabel>Youtube Link</StyledLinkLabel>
          {property.linkYoutube ? (
            <StyledLink href={property.linkYoutube} target="_blank" rel="noopener noreferrer">
              {property.linkYoutube}
            </StyledLink>
          ) : (
            <StyledLinkValue>--</StyledLinkValue>
          )}
        </StyledLinkContainer>
        <StyledGridSpan3>
          <StyledLinkContainer>
            <StyledLinkLabel>Google Map Link</StyledLinkLabel>
            {property.googleMapLink ? (
              <>
                {isResolvingMap ? (
                  <StyledMapContainer>
                    <StyledLoadingText>Loading map...</StyledLoadingText>
                  </StyledMapContainer>
                ) : resolvedMapUrl ? (
                  <StyledMapContainer>
                    <StyledMapIframe
                      src={resolvedMapUrl}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Property Location"
                    />
                  </StyledMapContainer>
                ) : (
                  <StyledLink href={property.googleMapLink} target="_blank" rel="noopener noreferrer">
                    {property.googleMapLink}
                  </StyledLink>
                )}
              </>
            ) : (
              <StyledLinkValue>--</StyledLinkValue>
            )}
          </StyledLinkContainer>
        </StyledGridSpan3>
        <StyledGridSpan3>
          <Textarea label="Description" name="description" value={property.description} disabled fullWidth rows={5} />
        </StyledGridSpan3>
        <StyledUploadSection span={3}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {property.images && property.images.length > 0 ? property.images.map((img, idx) => {
              let src = '';
              if (typeof img === 'string') src = buildImageUrl(img);
              else if (img instanceof File) src = URL.createObjectURL(img);
              return (
                <img
                  key={idx}
                  src={src}
                  alt={`Property ${idx + 1}`}
                  style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 6, border: '1px solid #ccc' }}
                />
              );
            }) : <span style={{ color: '#888' }}>No images</span>}
          </div>
        </StyledUploadSection>
        <StyledUploadSection span={3}>
          {property.coverImage ? (
            <img
              src={typeof property.coverImage === 'string' ? buildImageUrl(property.coverImage) : URL.createObjectURL(property.coverImage)}
              alt="Cover"
              style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, border: '1px solid #ccc' }}
            />
          ) : <span style={{ color: '#888' }}>No cover image</span>}
        </StyledUploadSection>
      </StyledFormGrid>
    </>
  );
};

const StyledFormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px 16px;
  margin-bottom: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px 12px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    margin-bottom: 16px;
  }
`;

const StyledUploadSection = styled.div<{ span?: number }>`
  grid-column: span ${props => props.span || 1};
  display: flex;
  gap: 32px;
  margin: 16px 0 0 0;
  align-items: flex-start;
  flex-wrap: wrap;
  
  @media (max-width: 1024px) {
    grid-column: span 1;
    gap: 20px;
  }
  
  @media (max-width: 768px) {
    gap: 20px;
    margin: 20px 0 0 0;
  }
  
  @media (max-width: 480px) {
    gap: 16px;
    margin: 16px 0 0 0;
  }
`;

const StyledGridSpan2 = styled.div`
  grid-column: span 2;
  
  @media (max-width: 1024px) {
    grid-column: span 1;
  }
`;

const StyledGridSpan3 = styled.div`
  grid-column: span 3;
  
  @media (max-width: 1024px) {
    grid-column: span 2;
  }
  
  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const StyledHeaderRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
  background: none;
  box-shadow: none;
  
  @media (max-width: 768px) {
    margin-bottom: 8px;
  }
`;

const StyledLinkContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const StyledLinkLabel = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-right: 5px;
`;

const StyledLink = styled.a`
  padding: 17px 16px;
  border-radius: 6px;
  border: 1px solid #cccccc;
  background-color: #f9f9f9;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 100%;
  color: #0066cc;
  text-decoration: none;
  word-break: break-all;
  cursor: pointer;
  
  &:hover {
    color: #004499;
    text-decoration: underline;
    background-color: #f0f4f8;
  }
`;

const StyledLinkValue = styled.div`
  padding: 17px 16px;
  border-radius: 6px;
  border: 1px solid #cccccc;
  background-color: #f9f9f9;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 100%;
  color: #333333;
`;

const StyledMapContainer = styled.div`
  margin-top: 12px;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #cccccc;
`;

const StyledMapIframe = styled.iframe`
  width: 100%;
  height: 300px;
  border: none;
  
  @media (max-width: 768px) {
    height: 250px;
  }
  
  @media (max-width: 480px) {
    height: 200px;
  }
`;

const StyledLoadingText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: #666;
  font-size: 14px;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    height: 250px;
  }
  
  @media (max-width: 480px) {
    height: 200px;
  }
`;